import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Plus, Trash2, Sparkles, ClipboardList } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { API } from '../../App';
import { fetchCoachAthletes } from '../../lib/coachAthletes';
import HamburgerMenu from '../../components/HamburgerMenu';

const emptyExercise = () => ({ exercise_name: '', muscle_group: '', sets: 3, reps: '8-12', notes: '' });

export default function PlanBuilder() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [workoutType, setWorkoutType] = useState('gym');
  const [focus, setFocus] = useState('Full Body');
  const [skill, setSkill] = useState('intermediate');
  const [notes, setNotes] = useState('');
  const [exercises, setExercises] = useState([emptyExercise()]);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selAthletes, setSelAthletes] = useState(new Set());
  const [selTeams, setSelTeams] = useState(new Set());
  const [plans, setPlans] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPlans = () => {
    axios.get(`${API}/coach/${orgId}/plans`).then((res) => setPlans(res.data.plans || [])).catch(() => {});
  };

  useEffect(() => {
    const controller = new AbortController();
    setAthletes([]);
    fetchCoachAthletes(axios, API, orgId, null, controller.signal)
      .then((rows) => { if (!controller.signal.aborted) setAthletes(rows); }).catch(() => {});
    axios.get(`${API}/orgs/${orgId}/teams`).then((res) => setTeams(res.data.teams || [])).catch(() => {});
    loadPlans();
    return () => controller.abort();
  }, [orgId]);

  const updateExercise = (i, key, val) => {
    setExercises((exs) => exs.map((e, idx) => (idx === i ? { ...e, [key]: val } : e)));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/coach/${orgId}/plans/generate`, {
        focus, workout_type: workoutType, skill_level: skill,
      });
      const gen = (res.data.exercises || []).map((e) => ({
        exercise_name: e.exercise_name || e.name || 'Exercise',
        muscle_group: e.muscle_group || e.muscle || '',
        sets: typeof e.sets === 'number' ? e.sets : Array.isArray(e.sets) ? e.sets.length : 3,
        reps: String(e.reps || '8-12'),
        notes: e.instructions ? String(e.instructions).slice(0, 140) : '',
      }));
      if (gen.length === 0) {
        toast.error('AI returned no exercises. Build manually.');
      } else {
        setExercises(gen);
        toast.success(`Generated ${gen.length} exercises. Edit freely before saving.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI generation unavailable. Build the plan manually.');
    } finally {
      setGenerating(false);
    }
  };

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const validExercises = exercises.filter((ex) => ex.exercise_name.trim());
    if (!name.trim()) { toast.error('Give the plan a name.'); return; }
    if (validExercises.length === 0) { toast.error('Add at least one exercise.'); return; }
    if (selAthletes.size === 0 && selTeams.size === 0) { toast.error('Assign the plan to at least one athlete or team.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/coach/${orgId}/plans`, {
        name: name.trim(),
        workout_type: workoutType,
        focus,
        notes: notes.trim() || null,
        exercises: validExercises.map((ex) => ({ ...ex, sets: parseInt(ex.sets) || 3 })),
        assigned_athlete_ids: Array.from(selAthletes),
        assigned_team_ids: Array.from(selTeams),
      });
      toast.success('Plan created and assigned.');
      setName(''); setNotes(''); setExercises([emptyExercise()]);
      setSelAthletes(new Set()); setSelTeams(new Set());
      loadPlans();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan.');
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (id) => {
    try {
      await axios.delete(`${API}/coach/${orgId}/plans/${id}`);
      setPlans((p) => p.filter((x) => x.id !== id));
      toast.success('Plan deleted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete plan.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900" data-testid="plan-builder-page">
      <HamburgerMenu />
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <Button variant="outline" onClick={() => navigate('/teams')} className="glass border-stone-700 text-stone-300 hover:bg-stone-800 mb-5" data-testid="plan-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-wider flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-amber-400" />WORKOUT PLAN BUILDER
          </h1>
          <p className="text-stone-400 text-sm mt-1">Build plans manually or with AI, add custom exercises, and assign to athletes or whole teams.</p>
        </div>

        <Card className="glass border-stone-800 p-5 mb-8">
          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Plan Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Off-Season Strength Block" maxLength={100} className="bg-stone-900 border-stone-700 text-white" data-testid="plan-name-input" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Environment</label>
                <Select value={workoutType} onValueChange={setWorkoutType}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="plan-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    <SelectItem value="gym" className="focus:bg-stone-800 focus:text-white">Gym</SelectItem>
                    <SelectItem value="home" className="focus:bg-stone-800 focus:text-white">Home</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Focus</label>
                <Select value={focus} onValueChange={setFocus}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="plan-focus-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    {['Full Body', 'Upper Body', 'Lower Body', 'Push', 'Pull', 'Legs', 'Core', 'Conditioning'].map((f) => (
                      <SelectItem key={f} value={f} className="focus:bg-stone-800 focus:text-white">{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Difficulty (for AI)</label>
                <Select value={skill} onValueChange={setSkill}>
                  <SelectTrigger className="bg-stone-900 border-stone-700 text-white" data-testid="plan-skill-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    {['beginner', 'intermediate', 'advanced'].map((s) => (
                      <SelectItem key={s} value={s} className="focus:bg-stone-800 focus:text-white">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button type="button" onClick={handleGenerate} disabled={generating} className="w-full bg-gradient-to-r from-amber-800 to-amber-950 hover:from-amber-700 hover:to-amber-900 border border-amber-700 text-white font-bold" data-testid="plan-generate-ai-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Generate with AI</>}
                </Button>
              </div>
            </div>

            {/* Exercises */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Exercises — custom names welcome</label>
              <div className="space-y-2">
                {exercises.map((ex, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`exercise-row-${i}`}>
                    <Input value={ex.exercise_name} onChange={(e) => updateExercise(i, 'exercise_name', e.target.value)} placeholder="Exercise name (any exercise)" className="col-span-4 bg-stone-900 border-stone-700 text-white text-sm" data-testid={`exercise-name-${i}`} />
                    <Input value={ex.muscle_group} onChange={(e) => updateExercise(i, 'muscle_group', e.target.value)} placeholder="Muscle group" className="col-span-3 bg-stone-900 border-stone-700 text-white text-sm" />
                    <Input type="number" min="1" max="10" value={ex.sets} onChange={(e) => updateExercise(i, 'sets', e.target.value)} className="col-span-2 bg-stone-900 border-stone-700 text-white text-sm" title="Sets" />
                    <Input value={ex.reps} onChange={(e) => updateExercise(i, 'reps', e.target.value)} placeholder="Reps" className="col-span-2 bg-stone-900 border-stone-700 text-white text-sm" />
                    <button type="button" onClick={() => setExercises((exs) => exs.filter((_, idx) => idx !== i))} className="col-span-1 text-stone-500 hover:text-blood-500 flex justify-center" aria-label="Remove exercise">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" onClick={() => setExercises((exs) => [...exs, emptyExercise()])} className="mt-2 border-stone-700 bg-stone-900 text-stone-300 hover:bg-stone-800 h-8 text-xs" data-testid="add-exercise-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Add Exercise
              </Button>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-stone-400 mb-1.5">Coach Notes (optional)</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={1000} placeholder="Tempo cues, rest times, focus points…" className="bg-stone-900 border-stone-700 text-white" data-testid="plan-notes-input" />
            </div>

            {/* Assignment */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Assign to Teams</label>
                {teams.length === 0 ? <p className="text-xs text-stone-500">No teams yet.</p> : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {teams.map((t) => (
                      <label key={t.id} className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                        <Checkbox checked={selTeams.has(t.id)} onCheckedChange={() => toggle(selTeams, setSelTeams, t.id)} className="border-stone-600 data-[state=checked]:bg-amber-700 data-[state=checked]:border-amber-600" data-testid={`assign-team-${t.id}`} />
                        {t.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-400 mb-2">Assign to Athletes</label>
                {athletes.length === 0 ? <p className="text-xs text-stone-500">No athletes yet — invite some first.</p> : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {athletes.map((a) => (
                      <label key={a.user_id} className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                        <Checkbox checked={selAthletes.has(a.user_id)} onCheckedChange={() => toggle(selAthletes, setSelAthletes, a.user_id)} className="border-stone-600 data-[state=checked]:bg-amber-700 data-[state=checked]:border-amber-600" data-testid={`assign-athlete-${a.user_id}`} />
                        {a.username}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button type="submit" disabled={saving} className="w-full bg-amber-700 hover:bg-amber-600 text-white font-bold py-5 uppercase tracking-widest" data-testid="plan-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & Assign Plan'}
            </Button>
          </form>
        </Card>

        {/* Existing plans */}
        <h2 className="text-lg font-bold text-white tracking-wider uppercase mb-3">Existing Plans</h2>
        {plans.length === 0 ? (
          <Card className="glass border-stone-800 p-6 text-center"><p className="text-stone-400 text-sm">No plans yet.</p></Card>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => (
              <Card key={p.id} className="glass border-stone-800 p-4 flex items-start justify-between gap-3" data-testid={`plan-card-${p.id}`}>
                <div className="min-w-0">
                  <span className="text-white font-bold">{p.name}</span>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {p.focus ? `${p.focus} · ` : ''}{(p.exercises || []).length} exercises · by {p.created_by_name}
                  </p>
                  <p className="text-[11px] text-amber-400/80 mt-1 truncate">
                    {[...(p.assigned_team_names || []).map((n) => `Team: ${n}`), ...(p.assigned_athlete_names || [])].join(', ') || 'Unassigned'}
                  </p>
                </div>
                <button type="button" onClick={() => deletePlan(p.id)} className="text-stone-500 hover:text-blood-500 shrink-0 p-1" aria-label="Delete plan" data-testid={`delete-plan-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
